import { RouteLocationNormalized } from 'vue-router';
import * as Namespaces from '~/api/services';
import { clearPendingRouteParams, setPendingRouteParams } from '~/api/services/request';
import { toast } from '~/composables/useToast';
import { Logger, Router } from '~/core';
import { TranslatableError } from '~/errors';
import { useLoadingStore, useModelsStore, useServerBlockStore } from '~/stores';

export class ModelBindings implements Middleware {
    name() {
        return `model_bindings(${this.namespace})`;
    }

    private currentNavigationId = 0;

    constructor(private namespace: string) {}

    async run(to: RouteLocationNormalized, from?: RouteLocationNormalized) {
        const navigationId = ++this.currentNavigationId;

        setPendingRouteParams(to.params as Record<string, any>);

        const modelsStore = useModelsStore();
        const loadingStore = useLoadingStore();
        const serverBlockStore = useServerBlockStore();

        const models: { id: string; name: string; get: () => Promise<any> }[] = [];
        for (const paramKey in to.params) {
            const serviceName = `${paramKey.substring(0, 1).toUpperCase() + paramKey.substring(1)}sService`;
            const service = (Namespaces as Record<string, any>)[this.namespace]?.[serviceName];
            if (service) {
                const getMethod = service.get;
                if (getMethod) {
                    models.push({
                        id: to.params[paramKey].toString(),
                        name: paramKey,
                        get: getMethod.bind(service),
                    });
                } else {
                    Logger.debug('ModelBindings', `Detected a possible model binding '${paramKey}', but it seems to have no 'get' method...?`);
                }
            }
        }

        // Check if we're switching between different namespaces (admin <-> client).
        // Route names don't map 1:1 to namespace — client-side routes start with
        // many prefixes (`server.*`, `account.*`, `index`, …) but only admin
        // routes start with `admin.`. Compare categories, not raw prefixes, or
        // we refetch on every intra-section nav.
        // this gives a fucking 50% speedup
        const toIsAdmin = to.name?.toString().startsWith('admin.') ?? false;
        const fromIsAdmin = from?.name?.toString().startsWith('admin.') ?? false;
        const namespaceSwitched = from?.name != null && fromIsAdmin !== toIsAdmin;

        const currentModels = models.map((a) => a.name);
        const allModels = modelsStore.models;
        const isClientServerRoute = this.namespace === 'client' && currentModels.includes('server');

        const modelsToDelete = Object.keys(allModels).filter((name) => !currentModels.includes(name));

        const modelsToFetch = models.filter((model) => {
            const existingModel = modelsStore.models[model.name as keyof typeof modelsStore.models];

            // Force refetch if switching namespaces or if normal conditions apply.
            //
            // Compare against the *slugged* route id, because that is what the
            // URL actually holds: the swap below rewrites the param to the slug
            // for models that route by one. Comparing the bare route id instead
            // never matches for a slugged model, so it refetched on every
            // intra-model navigation. `getSluggedRouteID` falls back to
            // `getRouteID` for models that don't slug, so id-routed models are
            // unaffected. (`getRouteID` takes no argument on any model -- the
            // namespace that used to be passed here was always ignored.)
            const shouldRefetchDueToNamespaceSwitch = namespaceSwitched && existingModel;
            const shouldRefetchDueToIdChange = !existingModel || (existingModel as any)?.getSluggedRouteID?.() !== model.id;

            return shouldRefetchDueToNamespaceSwitch || shouldRefetchDueToIdChange;
        });

        Logger.debug(
            'ModelBindings',
            `Navigation ${navigationId}: delete [${modelsToDelete.join(', ')}], fetch [${modelsToFetch.map((a) => a.name).join(', ')}], final [${models.map((a) => a.name).join(', ')}]`,
        );

        modelsToDelete.forEach((name) => {
            Logger.debug('ModelBindings', `Navigation ${navigationId}: Clearing model '${name}'`);
            modelsStore.clearModel(name as any);
        });

        if (!isClientServerRoute && serverBlockStore.isBlocked) {
            Logger.debug('ModelBindings', `Navigation ${navigationId}: Clearing server block state (target is not a client server route)`);
            serverBlockStore.clearBlock();
        }

        if (this.currentNavigationId !== navigationId) {
            Logger.debug('ModelBindings', `Navigation ${navigationId} superseded, aborting before fetch`);
            return;
        }

        // STEP 2: Fetch new models with proper await
        await Promise.all(
            modelsToFetch.map(async (model) => {
                const finished = loadingStore.add(`ModelBindings@${model.name}`);

                try {
                    const result = await model.get();

                    if (this.currentNavigationId !== navigationId) {
                        Logger.debug('ModelBindings', `Navigation ${navigationId} superseded, skipping setModel for '${model.name}'`);
                        return;
                    }

                    modelsStore.setModel({
                        name: model.name as any,
                        model: result,
                        refresh: model.get,
                    });

                    if (model.name === 'server' && serverBlockStore.isBlocked) {
                        Logger.debug('ModelBindings', `Navigation ${navigationId}: Clearing server block state after successful server fetch`);
                        serverBlockStore.clearBlock();
                    }

                    // Opt-in pretty-URLs: only models that route by slug (overriding
                    // getSlugID, e.g. servers) get their id swapped for a slug. Models
                    // bound by id on the backend (e.g. tags) must keep the id in the URL.
                    const slugId = result.getSlugID?.();
                    if (slugId && to.params[model.name] !== slugId) {
                        const newParams = { ...to.params, [model.name]: slugId };
                        Logger.debug(
                            'ModelBindings',
                            `Navigation ${navigationId}: Replacing route param '${model.name}' ID '${model.id}' with slug '${slugId}'`,
                        );
                        await Router.replace({ name: to.name?.toString() || '', params: newParams, query: to.query });
                    }
                } catch (err) {
                    if (err instanceof TranslatableError) {
                        const isServerError = err.title[0]?.startsWith('server.errors');
                        if (isServerError && model.name === 'server') {
                            serverBlockStore.setBlocked(err.title[0]);
                        } else {
                            // Other errors show as toast
                            const displayError = err.getDisplayError();
                            toast.error(displayError.title, displayError.messages);
                        }
                    }
                } finally {
                    finished();
                }
            }),
        );
    }

    async postRun() {
        clearPendingRouteParams();
    }
}
